import { useMutation, useQuery } from "@apollo/client";
import ScienceIcon from "@mui/icons-material/Science";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { CheckboxField } from "components/Forms/CheckboxField";
import {
  AutocompleteItem,
  FormAutocomplete,
} from "components/Forms/FormAutocomplete";
import { FormTextField } from "components/Forms/FormTextField";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { gql } from "generated-graphql";
import {
  TenantInput,
  CopyableDemoTenantDataType,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDebounce } from "hooks/useDebounce";
import { useOmnisearchDatagrid } from "hooks/useOmnisearchDatagrid";
import { useValidatingForm } from "hooks/useValidatingForm";
import { omit } from "lodash";
import { useEffect, useMemo, useState, useCallback } from "react";
import { Control, FormProvider } from "react-hook-form";
import { hasCriticalIssues } from "util/forms";
import { TENANTS_QUERY } from ".";
import { useTenantInputValidation } from "../validationHooks/useTenantInputValidation";

const UPSERT_TENANT_MUTATION = gql(`
  mutation UpsertTenant($input: TenantInput!) {
    upsertTenant(input: $input) {
      ...tenant
      demoDataJobQueued
    }
  }
`);

function CreateTenant() {
  const user = useCurrentUser();

  // State for keeping track of whether the create tenant dialog is open
  const [createTenantDialogOpen, setCreateTenantDialogOpen] = useState(false);

  // Only staff users can create tenants
  if (!user.isStaff) {
    return null;
  }

  return (
    <>
      <Button
        variant="contained"
        onClick={() => setCreateTenantDialogOpen(true)}
      >
        Create Organization
      </Button>
      <CreateTenantDialog
        open={createTenantDialogOpen}
        onClose={() => setCreateTenantDialogOpen(false)}
      />
    </>
  );
}

type CreateTenantDialogProps = {
  open: boolean;
  onClose: () => void;
};

type CreateTenantInput = TenantInput & {
  demoDataMode: "randomized" | "existing" | "none";
};

function CreateTenantDialog({ open, onClose }: CreateTenantDialogProps) {
  const theme = useTheme();
  const alerts = useAlerts();

  // only allow demo data creation in lower environments (not release)
  const showCreateDemoDataCheckbox = import.meta.env.VITE_ENV !== "release";

  // Form state/logic
  const defaultValues: CreateTenantInput = useMemo(
    () => ({
      name: "",
      isDisabled: false,
      isDemo: false,
      isPartner: false,
      populateWithDemoData: false,
      demoDataMode: "none", // Default to no demo data
      useDefaultNaming: true,
      copyWasteDemoTenantId: null,
    }),
    []
  );
  const transform = useCallback((input: CreateTenantInput) => {
    return {
      ...omit(input, "demoDataMode", "copyWasteDemoTenant"),
      // Explicitly set populateWithDemoData based on demoDataMode
      populateWithDemoData: input.demoDataMode !== "none",
      copyDemoTenantId:
        input.demoDataMode === "existing" ? input.copyDemoTenantId : null,
      copyWasteDemoTenantId: input.copyWasteDemoTenantId,
    };
  }, []);

  const validator = useTenantInputValidation();

  const transformedValidator = useCallback(
    (input: CreateTenantInput) => {
      return validator(transform(input));
    },
    [validator, transform]
  );

  const form = useValidatingForm<CreateTenantInput>(
    defaultValues,
    [],
    transformedValidator
  );

  const {
    control,
    formState: { isDirty },
    handleSubmit,
    issues,
    reset,
    setValue,
    watch,
  } = form;

  const demoDataMode = watch("demoDataMode");
  const isPartner = watch("isPartner");

  // If creating a partner organization, clear the partnerTenantId
  useEffect(() => {
    if (isPartner) {
      setValue("partnerTenantId", null);
    }
  }, [isPartner, setValue]);

  // Update populateWithDemoData when demoDataMode changes
  useEffect(() => {
    setValue("populateWithDemoData", demoDataMode !== "none");
  }, [demoDataMode, setValue]);

  // Tenants query and related state
  const {
    omnisearch,
    setOmnisearch,
    paginationModel: { page, pageSize },
    sortModel,
  } = useOmnisearchDatagrid({
    persistenceKey: "create-tenant-dialog",
    isURLDriven: false,
    initialPageSize: 20,
    initialSortModel: [{ field: "name", sort: "asc" }],
  });
  const [search] = useDebounce(`isPartner:true ${omnisearch}`, 150);
  const {
    data: tenantsData,
    loading: loadingTenants,
    previousData: previousTenants,
  } = useQuery(TENANTS_QUERY, {
    variables: {
      search,
      page,
      pageSize,
      sort: [...sortModel],
    },
  });

  const tenantsOptions = useMemo(() => {
    return loadingTenants
      ? previousTenants?.tenants.items.map((t) => ({
          value: t.id,
          label: t.name,
        })) ?? []
      : tenantsData?.tenants.items.map((t) => ({
          value: t.id,
          label: t.name,
        })) ?? [];
  }, [loadingTenants, previousTenants, tenantsData]);

  // Mutation
  const [upsertTenant, { loading: saving }] = useMutation(
    UPSERT_TENANT_MUTATION,
    {
      refetchQueries: ["Tenants"],
    }
  );

  // Dialog handlers
  async function handleSave(input: CreateTenantInput) {
    const transformedInput = transform(input);
    await upsertTenant({
      variables: {
        input: transformedInput,
      },
      onCompleted: (data) => {
        if (data.upsertTenant.demoDataJobQueued) {
          alerts.success("Demo data job queued");
        } else {
          alerts.success("Organization created successfully");
        }
        handleClose();
      },
      onError: () => {
        alerts.error("Failed to create the organization");
      },
    });
  }

  function handleClose() {
    reset();
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      title="Create Organization"
      primaryActionButtonProps={{
        onClick: handleSubmit(handleSave),
        saveText: "Create",
        loading: saving,
        disabled: !isDirty || hasCriticalIssues(issues),
      }}
    >
      <FormProvider {...form}>
        <FormTextField
          label="Organization Name"
          name={"name"}
          sx={{ marginTop: theme.spacing(1) }}
          control={control}
        />
        {showCreateDemoDataCheckbox && (
          <>
            <RadioGroupField
              direction="column"
              radioOptions={[
                { value: "none", label: "No demo data" },
                { value: "randomized", label: "Randomized demo data" },
                {
                  value: "existing",
                  label: "Data from existing demo organization",
                },
              ]}
              label="Demo Data Options"
              tooltip="Choose whether to create this organization with demo data. Demo data is useful for testing and demonstrations."
              name="demoDataMode"
              control={control}
              hideErrors
            />
            <DemoTenantSelect
              name="copyDemoTenantId"
              control={control}
              disabled={demoDataMode !== "existing"}
              dataType={CopyableDemoTenantDataType.ChemicalAndFacility}
            />
            {/* Only show the prefix checkbox when demo data is selected */}
            {demoDataMode !== "none" && (
              <CheckboxField
                label="Use the 'Dunder Mifflin Paper Company' prefix"
                name="useDefaultNaming"
                control={control}
                noErrorDisplay
              />
            )}
            <Box display="flex" alignItems="center" gap={1}>
              <ScienceIcon fontSize="small" />
              <Typography variant="body1">
                Copy Waste Data from Existing Organization
              </Typography>
            </Box>
            <Typography variant="caption" fontStyle="italic">
              We will anonymize the data the best we can, but, be sure to check
              profile numbers and names of waste streams for identifying
              information.
            </Typography>
            <DemoTenantSelect
              name="copyWasteDemoTenantId"
              control={control}
              dataType={CopyableDemoTenantDataType.Waste}
            />
          </>
        )}
        <CheckboxField
          label="Make this a Partner Organization"
          name={"isPartner"}
          control={control}
          noErrorDisplay
        />
        <FormAutocomplete
          label="Associate this Organization with a Partner Organization"
          name={"partnerTenantId"}
          autocompleteProps={{
            onInputChange: (_, newInputValue) => {
              setOmnisearch(newInputValue);
            },
            // This is required by the type but will be overwritten in the component
            renderInput: () => null,
            // This is required by the type but will be overwritten in the component
            options: tenantsOptions,
            loading: loadingTenants,
            loadingText: "Loading organizations...",
            disabled: isPartner,
          }}
          autocompleteItems={tenantsOptions}
          disabled={isPartner}
          control={control}
        />
      </FormProvider>
    </Dialog>
  );
}

export { CreateTenant };

const COPYABLE_DEMO_TENANTS_QUERY = gql(`
  query CopyableDemoTenants($input: CopyableDemoTenantInput!) {
    copyableDemoTenants(input: $input) {
      id
      name
    }
  }
`);

const DemoTenantSelect = ({
  name,
  control,
  disabled = false,
  dataType,
}: {
  name: string;
  control: Control<CreateTenantInput>;
  disabled?: boolean;
  dataType: CopyableDemoTenantDataType;
}) => {
  const { data, loading } = useQuery(COPYABLE_DEMO_TENANTS_QUERY, {
    variables: { input: { dataType } },
  });
  const tenants: AutocompleteItem[] =
    data?.copyableDemoTenants?.map((t) => ({
      value: t.id,
      label: t.name,
    })) ?? [];
  return (
    <FormAutocomplete
      label="Select a demo organization to copy"
      name={name as keyof CreateTenantInput}
      autocompleteProps={{
        renderInput: () => null,
        options: tenants,
        loading: loading,
        loadingText: "Loading organizations...",
      }}
      autocompleteItems={tenants}
      disabled={disabled}
      control={control}
    />
  );
};
